// Roles outside the model's disciplines (StaffPlan.otherRoles).
// Counted in the plan's headline totals, never in calculations.

import { Plus, Trash2 } from 'lucide-react';
import {
  LEVEL_IDS,
  STAFF_PLAN_OTHER_ROLE_OPTIONS,
  exactLevelLabel,
  resolveCellSplit,
} from '@rc/engine';
import type { LevelId, StaffPlanOtherRole } from '@rc/engine/types';

interface Props {
  roles: StaffPlanOtherRole[];
  /** When provided, rows are editable and can be added / removed. */
  onChange?: (roles: StaffPlanOtherRole[]) => void;
}

const DATALIST_ID = 'other-roles-table-options';

export default function OtherRolesTable({ roles, onChange }: Props) {
  const editable = !!onChange;

  const update = (idx: number, patch: Partial<StaffPlanOtherRole>) => {
    if (!onChange) return;
    onChange(
      roles.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r, ...patch };
        next.fte = (Number(next.onFte) || 0) + (Number(next.hubFte) || 0);
        return next;
      }),
    );
  };

  const grand = roles.reduce(
    (acc, r) => {
      const s = resolveCellSplit(r);
      acc.on += s.on;
      acc.hub += s.hub;
      acc.total += s.total;
      return acc;
    },
    { on: 0, hub: 0, total: 0 },
  );

  const numInput = 'input !px-1.5 !py-1 text-xs text-right tabular-nums w-20 ml-auto';

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Other roles · not modeled
          </div>
          <p className="text-[11px] text-slate-500">
            Roles without a model discipline. Included in plan totals; excluded from
            calculations.
          </p>
        </div>
        {editable && (
          <button
            type="button"
            className="btn-secondary !py-1 text-xs"
            onClick={() =>
              onChange!([...roles, { role: '', levelId: 'L3', fte: 0, onFte: 0, hubFte: 0 }])
            }
          >
            <Plus size={12} /> Add role
          </button>
        )}
      </div>
      {roles.length === 0 ? (
        <div className="px-3 py-4 text-xs text-slate-400">No other roles on this plan.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Level</th>
              <th className="text-right px-3 py-2">On</th>
              <th className="text-right px-3 py-2 bg-amber-50/60">Hub</th>
              <th className="text-right px-3 py-2">Total</th>
              {editable && <th className="w-10" />}
            </tr>
          </thead>
          <tbody>
            {roles.map((r, idx) => {
              const s = resolveCellSplit(r);
              return (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 text-xs font-medium text-slate-700">
                    {editable ? (
                      <input
                        className="input !px-2 !py-1 text-xs"
                        list={DATALIST_ID}
                        value={r.role}
                        placeholder="Pick or type a role"
                        onChange={(e) => update(idx, { role: e.target.value })}
                      />
                    ) : (
                      r.role
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-600">
                    {editable ? (
                      <select
                        className="input !px-1.5 !py-1 text-xs"
                        value={r.levelId}
                        onChange={(e) => update(idx, { levelId: e.target.value as LevelId })}
                      >
                        {LEVEL_IDS.map((id) => (
                          <option key={id} value={id}>
                            {exactLevelLabel(id)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      exactLevelLabel(r.levelId)
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs text-slate-700">
                    {editable ? (
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        className={numInput}
                        value={s.on}
                        onChange={(e) =>
                          update(idx, { onFte: Number(e.target.value) || 0, hubFte: s.hub })
                        }
                      />
                    ) : (
                      s.on.toFixed(1)
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs text-amber-900 bg-amber-50/30">
                    {editable ? (
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        className={numInput}
                        value={s.hub}
                        onChange={(e) =>
                          update(idx, { hubFte: Number(e.target.value) || 0, onFte: s.on })
                        }
                      />
                    ) : (
                      s.hub.toFixed(1)
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-slate-800">
                    {s.total.toFixed(1)}
                  </td>
                  {editable && (
                    <td className="px-2 py-1.5 text-center">
                      <button
                        type="button"
                        className="inline-flex text-slate-400 hover:text-rose-600"
                        title="Remove"
                        onClick={() => onChange!(roles.filter((_, i) => i !== idx))}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-100/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <td className="px-3 py-2 font-semibold text-slate-700" colSpan={2}>
                Other total
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                {grand.on.toFixed(1)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-900 bg-amber-50/60">
                {grand.hub.toFixed(1)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-800">
                {grand.total.toFixed(1)}
              </td>
              {editable && <td />}
            </tr>
          </tfoot>
        </table>
      )}
      {editable && (
        <datalist id={DATALIST_ID}>
          {STAFF_PLAN_OTHER_ROLE_OPTIONS.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      )}
    </div>
  );
}
